import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import api, { uploadFile } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { User, Lock, Phone, Mail, Save, Loader2, Eye, EyeOff, Upload, X, PenLine, Building2, Image as ImageIcon } from 'lucide-react';

const TITOLI_OPTIONS = [
  '', 'Sig.', 'Sig.ra', 'Dott.', 'Dott.ssa', 'Ing.', 'Geom.', 'Arch.', 'P.I.', 'Dr.', 'Prof.', 'Avv.', 'Rag.'
];

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const sigInputRef = useRef(null);

  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    titolo_professionale: user?.titolo_professionale || '',
    firma_autografa_url: user?.firma_autografa_url || '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // v108 — Intestazione personalizzata del consulente HACCP / studio
  const [intestazione, setIntestazione] = useState({
    studio_nome: '',
    studio_qualifica: '',
    studio_indirizzo: '',
    studio_telefono: '',
    studio_email: '',
    studio_pec: '',
    studio_sito_web: '',
    studio_piva: '',
    studio_cf: '',
    has_logo: false,
    studio_logo_filename: '',
  });
  const [intestazioneLoading, setIntestazioneLoading] = useState(false);
  const logoInputRef = useRef(null);

  const INTESTAZIONE_ROLES = [
    'super_admin', 'admin', 'amministratore', 'rspp',
    'consulente_haccp', 'haccp_admin', 'haccp_responsabile', 'studio_tecnico',
    'tecnico_collaboratore', 'creator', 'formatore',
  ];
  const canManageIntestazione = INTESTAZIONE_ROLES.includes(user?.role);

  useEffect(() => {
    if (!canManageIntestazione) return;
    api.get('/users/me/intestazione').then(r => setIntestazione(r.data || {})).catch(() => {});
  }, [canManageIntestazione]);

  const handleSaveIntestazione = async (e) => {
    e?.preventDefault();
    setIntestazioneLoading(true);
    try {
      const r = await api.put('/users/me/intestazione', intestazione);
      setIntestazione(r.data);
      toast.success('Intestazione salvata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio intestazione');
    } finally {
      setIntestazioneLoading(false);
    }
  };

  const handleUploadLogo = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Formato non valido. Usa PNG o JPG.');
      return;
    }
    if (file.size > 3 * 1024 * 1024) { toast.error('Max 3 MB'); return; }
    setIntestazioneLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/users/me/intestazione/logo`, {
        method: 'POST',
        body: fd,
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token') || ''}` },
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      const data = await r.json();
      setIntestazione(data);
      toast.success('Logo caricato');
    } catch (err) {
      toast.error(`Errore upload logo: ${err.message}`);
    } finally {
      setIntestazioneLoading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Rimuovere il logo dello studio?')) return;
    try {
      await api.delete('/users/me/intestazione/logo');
      setIntestazione(i => ({ ...i, has_logo: false, studio_logo_filename: '', studio_logo_url: '' }));
      toast.success('Logo rimosso');
    } catch { toast.error('Errore rimozione'); }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/users/${user.id}`, profileData);
      toast.success('Profilo aggiornato!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSignature = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Formato non valido. Usa PNG, JPG o WEBP.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('File troppo grande (max 4 MB)');
      return;
    }
    setLoading(true);
    try {
      const res = await uploadFile('signatures', file);
      const url = res.data.url;
      setProfileData((p) => ({ ...p, firma_autografa_url: url }));
      // Save immediately
      await api.put(`/users/${user.id}`, { firma_autografa_url: url });
      toast.success('Firma autografa caricata e salvata');
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error('Errore caricamento firma');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSignature = async () => {
    if (!window.confirm('Rimuovere la firma autografa?')) return;
    setLoading(true);
    try {
      await api.put(`/users/${user.id}`, { firma_autografa_url: '' });
      setProfileData((p) => ({ ...p, firma_autografa_url: '' }));
      toast.success('Firma rimossa');
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error('Errore rimozione');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Le password non coincidono');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/auth/change-password?current_password=${encodeURIComponent(passwordData.current_password)}&new_password=${encodeURIComponent(passwordData.new_password)}`);
      toast.success('Password aggiornata con successo!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <User className="w-6 h-6" />
          Il Mio Profilo
        </h1>
        <p className="text-muted-foreground">Gestisci le tue informazioni personali, titolo e firma autografa</p>
      </div>

      {/* Profile Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informazioni Personali</CardTitle>
          <CardDescription>
            Titolo e nome saranno usati nei documenti DOCX generati (es. <em>Ing. Mario Rossi</em>).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{user?.email}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Ruolo</Label>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="capitalize">{user?.role?.replace('_', ' ')}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="titolo">Titolo</Label>
                <Select
                  value={profileData.titolo_professionale || '__none__'}
                  onValueChange={(v) => setProfileData({ ...profileData, titolo_professionale: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger id="titolo" data-testid="profile-titolo-select">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessuno —</SelectItem>
                    {TITOLI_OPTIONS.filter(t => t).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="full_name">Nome Completo</Label>
                <Input
                  id="full_name"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                  placeholder="Il tuo nome"
                  data-testid="profile-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefono</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  placeholder="+39 333 1234567"
                  className="pl-10"
                />
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="profile-save-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salva Modifiche
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Firma Autografa */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PenLine className="w-5 h-5" />
            Firma Autografa
          </CardTitle>
          <CardDescription>
            Carica un'immagine della tua firma (PNG/JPG, sfondo trasparente consigliato).
            Sarà inserita automaticamente nei documenti generati dove è presente il segnaposto
            <code className="px-1 mx-1 bg-muted rounded">«Firma»</code> o <code className="px-1 bg-muted rounded">«Firma_RSPP»</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {profileData.firma_autografa_url ? (
            <div className="flex items-center gap-4 p-3 border rounded-md bg-white">
              <img
                src={profileData.firma_autografa_url}
                alt="Firma"
                className="h-20 max-w-[260px] object-contain"
                data-testid="profile-signature-preview"
              />
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => sigInputRef.current?.click()}
                  data-testid="profile-signature-replace-btn"
                >
                  <Upload className="w-4 h-4 mr-1" /> Sostituisci
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveSignature}
                  className="text-red-500"
                  data-testid="profile-signature-remove-btn"
                >
                  <X className="w-4 h-4 mr-1" /> Rimuovi
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => sigInputRef.current?.click()}
              disabled={loading}
              data-testid="profile-signature-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Carica firma autografa
            </Button>
          )}
          <input
            ref={sigInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUploadSignature(f);
              e.target.value = '';
            }}
          />
        </CardContent>
      </Card>

      {/* v108 — INTESTAZIONE STUDIO PERSONALIZZATA */}
      {canManageIntestazione && (
        <Card data-testid="intestazione-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Intestazione Studio (per Verbali di Prelievo)
            </CardTitle>
            <CardDescription>
              Questi dati appariranno nell'intestazione (testata) di ogni Verbale di Prelievo HACCP
              che generi. Ogni consulente ha la propria intestazione personalizzata.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveIntestazione} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Nome studio / consulente *</Label>
                  <Input
                    value={intestazione.studio_nome || ''}
                    onChange={(e) => setIntestazione(i => ({ ...i, studio_nome: e.target.value }))}
                    placeholder="es. Dott. Mario Rossi · Studio Bianchi & Associati"
                    data-testid="int-nome"
                  />
                </div>
                <div>
                  <Label>Qualifica / titolo</Label>
                  <Input
                    value={intestazione.studio_qualifica || ''}
                    onChange={(e) => setIntestazione(i => ({ ...i, studio_qualifica: e.target.value }))}
                    placeholder="es. Consulente Sanitario ed H.A.C.C.P."
                    data-testid="int-qualifica"
                  />
                </div>
              </div>
              <div>
                <Label>Indirizzo studio</Label>
                <Input
                  value={intestazione.studio_indirizzo || ''}
                  onChange={(e) => setIntestazione(i => ({ ...i, studio_indirizzo: e.target.value }))}
                  placeholder="es. Via Roma, 42 — 98100 Messina (ME)"
                  data-testid="int-indirizzo"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Telefono</Label>
                  <Input
                    value={intestazione.studio_telefono || ''}
                    onChange={(e) => setIntestazione(i => ({ ...i, studio_telefono: e.target.value }))}
                    data-testid="int-tel"
                  />
                </div>
                <div>
                  <Label>Email pubblica</Label>
                  <Input
                    type="email"
                    value={intestazione.studio_email || ''}
                    onChange={(e) => setIntestazione(i => ({ ...i, studio_email: e.target.value }))}
                    data-testid="int-email"
                  />
                </div>
                <div>
                  <Label>PEC</Label>
                  <Input
                    type="email"
                    value={intestazione.studio_pec || ''}
                    onChange={(e) => setIntestazione(i => ({ ...i, studio_pec: e.target.value }))}
                    data-testid="int-pec"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Sito web</Label>
                  <Input
                    value={intestazione.studio_sito_web || ''}
                    onChange={(e) => setIntestazione(i => ({ ...i, studio_sito_web: e.target.value }))}
                    placeholder="https://"
                    data-testid="int-sito"
                  />
                </div>
                <div>
                  <Label>Partita IVA</Label>
                  <Input
                    value={intestazione.studio_piva || ''}
                    onChange={(e) => setIntestazione(i => ({ ...i, studio_piva: e.target.value }))}
                    data-testid="int-piva"
                  />
                </div>
                <div>
                  <Label>Codice Fiscale</Label>
                  <Input
                    value={intestazione.studio_cf || ''}
                    onChange={(e) => setIntestazione(i => ({ ...i, studio_cf: e.target.value }))}
                    data-testid="int-cf"
                  />
                </div>
              </div>

              {/* Logo */}
              <div className="border-t pt-3">
                <Label className="flex items-center gap-2"><ImageIcon className="w-4 h-4 text-indigo-600"/>Logo dello studio (opzionale)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  PNG o JPG, max 3 MB — apparirà sopra il nome dello studio nel verbale.
                </p>
                {intestazione.has_logo ? (
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded p-2">
                    <ImageIcon className="w-4 h-4 text-indigo-700"/>
                    <span className="text-sm text-indigo-900 flex-1 truncate">
                      {intestazione.studio_logo_filename || 'logo caricato'}
                    </span>
                    <Button type="button" size="sm" variant="ghost" onClick={() => logoInputRef.current?.click()} data-testid="int-logo-replace">
                      <Upload className="w-3 h-3 mr-1"/>Sostituisci
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={handleDeleteLogo} data-testid="int-logo-del">
                      <X className="w-3 h-3"/>
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} data-testid="int-logo-up">
                    <Upload className="w-4 h-4 mr-2"/>Carica logo
                  </Button>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUploadLogo(f);
                    e.target.value = '';
                  }}
                />
              </div>

              <Button type="submit" disabled={intestazioneLoading} data-testid="int-save">
                {intestazioneLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Save className="w-4 h-4 mr-2"/>}
                Salva intestazione
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Cambia Password
          </CardTitle>
          <CardDescription>Aggiorna la password del tuo account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Password Attuale</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Inserisci la password attuale"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">Nuova Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Inserisci la nuova password"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Conferma Nuova Password</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Conferma la nuova password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Cambia Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
